import { GraphType } from "utils/enums/graph";
import { IChartGroups } from "utils/interfaces/report";
import React from "react";
import {
  Bar,
  BarChart,
  Label,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CustomLegend from "./customLegend";

interface CustomTooltipProps {
  active?: boolean;
  payload?: {
    color: string;
    name: string;
    value: string;
  }[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* Tooltip Box */}
      <div
        style={{
          background: "#fff",
          borderRadius: 10,
          padding: "12px 16px",
          boxShadow: "0px 6px 20px 0px #01010129",
          fontSize: 13,
          color: "#333",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: 8 }}>{label}</div>
        {payload.map((entry, index) => (
          <div
            key={`${index + 1}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 6,
              gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: entry.color,
                  borderRadius: 2,
                }}
              />
              <span>{entry.name}</span>
            </div>
            <span style={{ fontWeight: 600 }}>{entry.value}%</span>
          </div>
        ))}
      </div>

      {/* V-shape Pointer with seamless join */}
      <div
        style={{
          position: "absolute",
          top: "calc(100% - 2px)",
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "15px solid transparent",
          borderRight: "15px solid transparent",
          borderTop: "15px solid #fff",
          zIndex: 1,
          filter: "drop-shadow(0px 6px 20px 0px #01010129)",
        }}
      />
    </div>
  );
};

interface ICommonBarCharProps {
  percentageData: IChartGroups[];
  categories: string[];
  graphType: GraphType;
  isAnimationActive: boolean;
  colorValueMap: Record<string, string>;
  totalEnrolments: number;
  isPrintComponent?: boolean;
}

const CommonBarChart: React.FC<ICommonBarCharProps> = (props) => {
  const {
    graphType,
    percentageData,
    categories,
    isAnimationActive,
    colorValueMap,
    totalEnrolments,
    isPrintComponent,
  } = props;

  const chartWidth = percentageData.length * categories.length * 26; // adjust 30 for wider spacing

  return (
    <div
      style={
        isPrintComponent ? undefined : { width: chartWidth, minWidth: "100%" }
      }
    >
      <ResponsiveContainer
        height={graphType === GraphType.SiteService ? 550 : 470}
      >
        <BarChart
          width={isPrintComponent ? undefined : chartWidth}
          data={percentageData}
          margin={{
            top: 25,
            right: 20,
            left: 20,
            bottom: graphType === GraphType.SiteService ? 150 : 70,
          }}
          barGap={6}
          barCategoryGap={100}
        >
          <XAxis
            dataKey="groupName"
            textAnchor="end"
            interval={0}
            height={70}
            tick={{
              fontSize: 12,
              dy: graphType === GraphType.SiteService ? 175 : 110,
              textAnchor: "middle",
            }}
            tickLine={false}
            fontSize={12}
            fontWeight={600}
          >
            {/* <Label
              value="Terms"
              position="insideBottom"
              offset={-70}
              fontSize={13}
              fontWeight={500}
            /> */}
          </XAxis>
          <YAxis
            domain={[0, 100]}
            tickFormatter={(value) => `${value}%`}
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickCount={6}
          >
            <Label
              value="Percentage of Students (%)"
              angle={-90}
              position="insideLeft"
              offset={0}
              textAnchor="middle"
              fontSize={12}
              fontWeight={600}
              style={{
                textAnchor: "middle",
              }}
            />
          </YAxis>
          <Tooltip
            cursor={false}
            content={<CustomTooltip />}
            animationEasing="linear"
            separator="-"
            offset={-100}
          />

          {isPrintComponent && (
            <Legend
              wrapperStyle={{
                paddingBottom: "30px",
              }}
              verticalAlign="top"
              content={() => (
                <CustomLegend
                  totalEnrolments={totalEnrolments}
                  colorValueMap={colorValueMap}
                />
              )}
            />
          )}

          {categories.map((key) => (
            <Bar
              key={key}
              dataKey={key}
              fill={colorValueMap[key]}
              radius={[4, 4, 0, 0]}
              maxBarSize={16}
              barSize={16}
              isAnimationActive={isAnimationActive}
            >
              <LabelList
                dataKey={key}
                content={({ x, y, width, value }) => {
                  const cx = Number(x) + Number(width) / 2 - 5;
                  // push label slightly down from top of bar
                  const cy = Number(y) - 15;

                  return (
                    <text
                      x={cx}
                      y={cy}
                      transform={`rotate(-90, ${cx}, ${cy})`}
                      fill="#000"
                      fontSize={10}
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="hanging"
                      underlinePosition={50}
                    >
                      {value || ""}
                    </text>
                  );
                }}
              />
              {/* Custom vertical label centered in the bar */}
              <LabelList
                dataKey={key}
                content={({ x, y, width, height }) => {
                  const cx = Number(x) + Number(width) / 2;
                  const cy = Number(y) + Number(height) + 8;

                  return (
                    <text
                      x={cx}
                      y={cy}
                      transform={`rotate(-90, ${cx}, ${cy})`}
                      fill="#757575"
                      fontSize={11}
                      textAnchor="end"
                      dominantBaseline="middle"
                    >
                      {key}
                    </text>
                  );
                }}
              />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CommonBarChart;
